import dayjs from 'dayjs';
import type {
  DirectAssignRequestDistributionRequestInDTO,
  GlobalTimeslots,
  LovDTO,
  ShipmentStateCodeEnum,
  MassActionSupplierNotificationInDTO,
} from '@api/ringil-api.v2';
import { apiPragueFromDayjsLocal, stringYMDFromDayjs } from '@utils/dateWithTz';
import { CarrierDistributionSelectorData, getSelectedUserIds } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';

/**
 * RD-3062 — the form → API mapping of `MultiEditSupplierNotificationSidebar`, moved out of the component unchanged
 * so it can be unit-tested. The component keeps the hooks, API call and UI; this is pure.
 */

export interface SupplierNotificationMassEditData {
  supplierDeliverySlots?: GlobalTimeslots;
  customerDeliverySlots?: GlobalTimeslots;
  responsiblePersonId: string;
  shipmentStateCode: ShipmentStateCodeEnum;

  overwriteLoadingTimeslots: boolean;
  overwriteShipmentStateCode: boolean;
  overwriteResponsiblePersonId: boolean;

  overwriteDatesWithOpeningTimes: boolean;
  loadingDates: Date[];
  unloadingDates: Date[];
  overwriteLabels: boolean;
  labels: string[];
  overwriteFolder: boolean;
  folders?: LovDTO[];
  createExpedition: boolean;
  distribution?: CarrierDistributionSelectorData;

}

export const fixRangeDate = (d: Date[]) => {
  if (d.length == 1) {
    return [d[0], d[0]];
  } else if (d.length == 2 && d[1] == null) {
    return [d[0], d[0]];
  }
  return [d[0], d[1]];
};

export const hasDateValue = (d: Date[]) => d && d.length > 0 && d[0] != null;

/** Body of `updateSupplierNotificationMassAction`; only the fields whose overwrite switch is on carry a value. */
export const buildMassEditRequest = (supplierNotificationIds: string[], values: SupplierNotificationMassEditData): MassActionSupplierNotificationInDTO => ({
  snIds: supplierNotificationIds,
  internalData: {
    loadingDeliverySlot: values.overwriteLoadingTimeslots ? values.supplierDeliverySlots : null,
    unloadingDeliverySlot: values.overwriteLoadingTimeslots ? values.customerDeliverySlots : null,
    responsiblePersonId: values.overwriteResponsiblePersonId ? values.responsiblePersonId : null,
    shipmentStateCode: values.overwriteShipmentStateCode ? values.shipmentStateCode : null,
    overwriteLoadingTimeslots: values.overwriteLoadingTimeslots,
    overwriteResponsiblePersonId: values.overwriteResponsiblePersonId,
    overwriteShipmentStateCode: values.overwriteShipmentStateCode,
    overwriteDatesWithOpeningTimes: values.overwriteDatesWithOpeningTimes,
    // legacy fields — Prague TZ datetime string
    loadingDatesSince: hasDateValue(values.loadingDates) ? apiPragueFromDayjsLocal(dayjs(fixRangeDate(values.loadingDates)[0])) : null,
    loadingDatesUntil: hasDateValue(values.loadingDates) ? apiPragueFromDayjsLocal(dayjs(fixRangeDate(values.loadingDates)[1])) : null,
    unloadingDatesSince: hasDateValue(values.unloadingDates) ? apiPragueFromDayjsLocal(dayjs(fixRangeDate(values.unloadingDates)[0])) : null,
    unloadingDatesUntil: hasDateValue(values.unloadingDates) ? apiPragueFromDayjsLocal(dayjs(fixRangeDate(values.unloadingDates)[1])) : null,
    // new fields — plain LocalDate string YYYY-MM-DD
    loadingLocalDateSince: hasDateValue(values.loadingDates) ? stringYMDFromDayjs(dayjs(fixRangeDate(values.loadingDates)[0])) : null,
    loadingLocalDateUntil: hasDateValue(values.loadingDates) ? stringYMDFromDayjs(dayjs(fixRangeDate(values.loadingDates)[1])) : null,
    unloadingLocalDateSince: hasDateValue(values.unloadingDates) ? stringYMDFromDayjs(dayjs(fixRangeDate(values.unloadingDates)[0])) : null,
    unloadingLocalDateUntil: hasDateValue(values.unloadingDates) ? stringYMDFromDayjs(dayjs(fixRangeDate(values.unloadingDates)[1])) : null,
    overwriteLabels: values.overwriteLabels,
    labels: values.overwriteLabels ? values.labels : null,
    overwriteFolder: values.overwriteFolder,
    folderIds: values.overwriteFolder ? (values.folders ?? []).map(f => f.id) : null,
    createExpedition: values.createExpedition,
    distribution: values.createExpedition ? {
      carrierId: values.distribution.selectedItems[0].id,
      price: values.distribution.price,
      notifyAssignedCarrier: values.distribution.notifyAssignedCarrier,
      userIds: getSelectedUserIds(values.distribution.selectedItems),
    } as DirectAssignRequestDistributionRequestInDTO : null,
  },
});
